import React from "react";

import { Box, Button, LinearProgress, Stack, SxProps, Theme, Typography } from "@mui/material";
import { useAccountSelector } from "store";

interface FreemiumUpgradeCardProps {
  onUpgrade: () => void;
  sx?: SxProps<Theme>;
}

export const FreemiumUpgradeCard: React.FC<FreemiumUpgradeCardProps> = ({ onUpgrade, sx }) => {
  const account = useAccountSelector();
  const { freemium_account_settings } = account || {};

  const dealsUsed = freemium_account_settings?.number_of_deals ?? 0;
  const dealsMax = freemium_account_settings?.max_deals ?? 15;
  const dataroomsMax = freemium_account_settings?.max_datarooms_connections ?? 1;
  const dataroomsUsed = freemium_account_settings?.number_of_datarooms_connections ?? 0;
  const filesMax = freemium_account_settings?.max_files ?? 20;
  const filesUsed = freemium_account_settings?.number_of_files ?? 0;
  const dealsProgress = dealsMax > 0 ? Math.min((dealsUsed / dealsMax) * 100, 100) : 0;
  const dataroomsProgress = dataroomsMax > 0 ? Math.min((dataroomsUsed / dataroomsMax) * 100, 100) : 0;
  const filesProgress = filesMax > 0 ? Math.min((filesUsed / filesMax) * 100, 100) : 0;
  return (
    <Box
      sx={{
        backgroundColor: "#496682",
        borderRadius: "8px",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        ...sx
      }}
    >
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          whiteSpace="nowrap"
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: "#ffffff",
            letterSpacing: "0.5px"
          }}
        >
          Platform Usage
        </Typography>
        <Button
          variant="contained"
          onClick={onUpgrade}
          sx={{
            backgroundColor: "#c09560",
            color: "#ffffff",
            fontSize: "13px",
            fontWeight: 400,
            textTransform: "none",
            width: "61px",
            height: "24px",
            "&:hover": {
              backgroundColor: "#b08550"
            }
          }}
        >
          Upgrade
        </Button>
      </Stack>

      {/* Usage Stats */}
      <Stack gap="12px">
        {/* Deals */}
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <Typography sx={{ fontSize: "12px", color: "#92a7bc" }}>Deals</Typography>
            <Typography sx={{ fontSize: "12px", color: "#92a7bc" }}>
              {dealsUsed}/{dealsMax}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={dealsProgress}
            sx={{
              height: "6px",
              borderRadius: "3px",
              backgroundColor: "#071f35",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#92a7bc",
                borderRadius: "3px"
              }
            }}
          />
        </Box>

        {/* Files - Coming Soon */}
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <Typography sx={{ fontSize: "12px", color: "#92a7bc" }}>Files</Typography>
            <Typography sx={{ fontSize: "12px", color: "#92a7bc" }}>
              {filesUsed}/{filesMax}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={filesProgress}
            sx={{
              height: "6px",
              borderRadius: "3px",
              backgroundColor: "#071f35",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#92a7bc",
                borderRadius: "3px"
              }
            }}
          />
        </Box>

        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <Typography sx={{ fontSize: "12px", color: "#92a7bc" }}>Integrations</Typography>
            <Typography sx={{ fontSize: "12px", color: "#92a7bc" }}>
              {dataroomsUsed}/{dataroomsMax}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={dataroomsProgress}
            sx={{
              height: "6px",
              borderRadius: "3px",
              backgroundColor: "#071f35",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#92a7bc",
                borderRadius: "3px"
              }
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
};
